import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { MRT_ColumnDef } from "material-react-table"
import { Grid, MenuItem } from "@mui/material"
import {
    PosSettingQuery,
    PosTemplatesForSupplierAdminResponse,
    fetchPosTemplatesForSupplier,
} from "../supplierPosSettingsApi"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { getEditSupplierTemplateRoute } from "../../../../../../constants/routes"
import { Template, TemplateStatus } from "../supplierPosSettingsTypes"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"

import { useSupplier } from "../../../../supplierHooks"
import { DestructiveMenuItem } from "../../../../../../shared/components/menus/DestructiveMenuItem"

const selectDevices = (data: PosTemplatesForSupplierAdminResponse) => data?.templates || []

export function SupplierPosSettingsTemplates() {
    const { supplierId } = useSupplier()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()

    const posTemplatesResult = useApiQuery(fetchPosTemplatesForSupplier, {
        queryName: PosSettingQuery.TEMPLATES_LIST,
        dependencies: {
            supplierId,
        },
        select: selectDevices,
    })
    const { ApiQueryTable, columnFactory } = useApiQueryTable(posTemplatesResult)

    const columns = useMemo<MRT_ColumnDef<Template>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("supplier-admin-pos-settings-templates-table-header-name"),
                accessorKey: "name",
            }),
            columnFactory.typeColumn({
                header: l10n.getString("supplier-admin-pos-settings-templates-table-header-language"),
                accessorKey: "languageCode",
                accessorFn: (row) => row?.languageCode.toUpperCase(),
            }),
            columnFactory.statusColumn({
                header: l10n.getString("supplier-admin-pos-settings-templates-table-header-status"),
                accessorFn: (originalRow) => (originalRow.status === TemplateStatus.IN_USE ? "yes" : "no"), // must be a string
                Cell: ({ row }) => {
                    if (row.original.status === TemplateStatus.IN_USE) {
                        return (
                            <LocalizedStrict id="supplier-admin-pos-settings-templates-table-status-column-active">
                                <StandardChip variant="success" placement="table">
                                    In use
                                </StandardChip>
                            </LocalizedStrict>
                        )
                    } else {
                        return (
                            <LocalizedStrict id="supplier-admin-pos-settings-templates-table-status-column-inactive">
                                <StandardChip variant="red" placement="table">
                                    Not in use
                                </StandardChip>
                            </LocalizedStrict>
                        )
                    }
                },
                filterSelectOptions: [
                    {
                        value: "yes",
                        text: l10n.getString(
                            "supplier-admin-pos-settings-templates-table-status-column-active-filter-yes",
                        ),
                    },
                    {
                        value: "no",
                        text: l10n.getString(
                            "supplier-admin-pos-settings-templates-table-status-column-inactive-filter-no",
                        ),
                    },
                ],
            }),
        ],
        [l10n, columnFactory],
    )

    return (
        <Grid container direction="column">
            <ApiQueryTable
                columns={columns}
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="supplier-admin-pos-settings-templates-table-edit-button">
                        <MenuItem
                            onClick={() => {
                                navigate(getEditSupplierTemplateRoute(row.original.id))
                                closeMenu()
                            }}
                            key={`edit-${row.original.id}`}
                        >
                            Edit
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="supplier-admin-pos-settings-templates-table-duplicate-button">
                        <MenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                            key={`duplicate-${row.original.id}`}
                        >
                            Duplicate
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="supplier-admin-pos-settings-templates-table-delete-button">
                        <DestructiveMenuItem
                            onClick={() => {
                                closeMenu()
                            }}
                            key={`delete-${row.original.id}`}
                        >
                            Delete
                        </DestructiveMenuItem>
                    </LocalizedStrict>,
                ]}
            />
        </Grid>
    )
}
